// Period-End FX Revaluation page
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    SimpleBarChart, StatusPill, fmtMoney, fmtDate, CHART_COLORS,
} from '../agents/AgentUIKit';
import { listFxRevaluations, runFxRevaluation, postFxRevaluation, listEntities } from './api';

export default function FXRevaluation() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [revals, setRevals] = useState([]);
    const [entities, setEntities] = useState([]);
    const [selectedReval, setSelectedReval] = useState(null);

    // Create dialog
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({
        entity_id: '',
        period_end_date: new Date().toISOString().slice(0, 10),
        accounts: [],
    });
    const [acctInput, setAcctInput] = useState({
        account_id: '', foreign_balance: '', foreign_currency: 'EUR', rate_old: '',
    });

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadAll = async () => {
        const [r1, r2] = await Promise.allSettled([listFxRevaluations(), listEntities()]);
        if (r1.status === 'fulfilled' && r1.value?.data?.success) setRevals(r1.value.data.data);
        if (r2.status === 'fulfilled' && r2.value?.data?.success) setEntities(r2.value.data.data);
    };

    useEffect(() => { run(loadAll); }, []);  // eslint-disable-line

    const addAccount = () => {
        if (!acctInput.account_id || !acctInput.foreign_balance) return;
        setForm({
            ...form,
            accounts: [...form.accounts, {
                account_id: parseInt(acctInput.account_id),
                foreign_balance: parseFloat(acctInput.foreign_balance),
                foreign_currency: acctInput.foreign_currency.toUpperCase(),
                rate_old: parseFloat(acctInput.rate_old || '1'),
            }],
        });
        setAcctInput({ account_id: '', foreign_balance: '', foreign_currency: acctInput.foreign_currency, rate_old: '' });
    };

    const removeAccount = (idx) => {
        setForm({ ...form, accounts: form.accounts.filter((_, i) => i !== idx) });
    };

    const runReval = async () => {
        if (!form.entity_id || !form.period_end_date || form.accounts.length === 0) return;
        const r = await run(() => runFxRevaluation(form));
        if (r?.data?.success) {
            setSelectedReval(r.data.data);
            setDialog(false);
            setForm({ entity_id: '', period_end_date: new Date().toISOString().slice(0, 10), accounts: [] });
            await loadAll();
        }
    };

    const handlePost = async (id) => {
        const r = await run(() => postFxRevaluation(id));
        if (r?.data?.success) await loadAll();
    };

    const entityLabel = (id) => {
        const e = entities.find(x => x.id === id);
        return e ? `${e.entity_code} — ${e.entity_name}` : `#${id}`;
    };

    const summaryCards = [
        { label: 'Total Revaluations', value: revals.length, accent: 'primary' },
        { label: 'Draft', value: revals.filter(r => r.status === 'DRAFT').length, accent: 'warning' },
        { label: 'Posted', value: revals.filter(r => r.status === 'POSTED').length, accent: 'success' },
        { label: 'YTD Net G/L', value: fmtMoney(revals.reduce((s, r) => s + (r.net_gain_loss || 0), 0)), accent: 'info' },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>FX Revaluation</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Period-end unrealized FX gain/loss on foreign-currency balances
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={() => run(loadAll)}><RefreshIcon /></IconButton>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog(true)}>
                            New Revaluation
                        </Button>
                    </Stack>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={summaryCards} />
                <Divider />

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader title="Revaluation Runs" />
                    {loading && !revals.length ? <LoadingBlock rows={4} /> : (
                        <DrillTable
                            columns={[
                                { key: 'period_end_date', label: 'Period End', render: r => fmtDate(r.period_end_date) },
                                { key: 'entity_id', label: 'Entity', render: r => entityLabel(r.entity_id) },
                                { key: 'functional_currency', label: 'Func Ccy' },
                                { key: 'total_unrealized_gain', label: 'Gain', align: 'right', render: r => <span style={{ color: CHART_COLORS.success }}>{fmtMoney(r.total_unrealized_gain)}</span> },
                                { key: 'total_unrealized_loss', label: 'Loss', align: 'right', render: r => <span style={{ color: CHART_COLORS.danger }}>{fmtMoney(r.total_unrealized_loss)}</span> },
                                { key: 'net_gain_loss', label: 'Net G/L', align: 'right', render: r => <b style={{ color: r.net_gain_loss >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>{fmtMoney(r.net_gain_loss)}</b> },
                                { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                { key: 'actions', label: '', render: r => (
                                    <Stack direction="row" spacing={0.5}>
                                        <Button size="small" onClick={() => setSelectedReval(r)}>View</Button>
                                        {r.status === 'DRAFT' && <Button size="small" variant="outlined" onClick={() => handlePost(r.id)}>Post</Button>}
                                    </Stack>
                                )},
                            ]}
                            rows={revals}
                            maxHeight={420}
                            rowKeyField="id"
                            onRowClick={setSelectedReval}
                        />
                    )}
                </Paper>

                {selectedReval && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <SectionHeader
                            title={`Revaluation #${selectedReval.id}`}
                            subtitle={`${entityLabel(selectedReval.entity_id)} | Period End: ${fmtDate(selectedReval.period_end_date)}`}
                        />
                        <SummaryGrid cards={[
                            { label: 'Unrealized Gain', value: fmtMoney(selectedReval.total_unrealized_gain), accent: 'success' },
                            { label: 'Unrealized Loss', value: fmtMoney(selectedReval.total_unrealized_loss), accent: 'danger' },
                            { label: 'Net G/L', value: fmtMoney(selectedReval.net_gain_loss), accent: selectedReval.net_gain_loss >= 0 ? 'success' : 'danger' },
                        ]} cols={3} />
                        {(selectedReval.accounts_revalued_json || []).length > 0 && (
                            <>
                                <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                                    <Typography variant="caption" fontWeight={600}>Gain/Loss by Account</Typography>
                                    <SimpleBarChart
                                        data={selectedReval.accounts_revalued_json.map(a => ({
                                            account: `#${a.account_id}`,
                                            gain_loss: a.gain_loss,
                                        }))}
                                        xKey="account"
                                        series={[{ key: 'gain_loss', label: 'G/L', color: CHART_COLORS.primary }]}
                                        height={220}
                                        yFormatter={v => fmtMoney(v)}
                                        showLegend={false}
                                    />
                                </Paper>
                                <DrillTable
                                    columns={[
                                        { key: 'account_id', label: 'Account' },
                                        { key: 'foreign_currency', label: 'Ccy' },
                                        { key: 'foreign_balance', label: 'Foreign Balance', align: 'right', render: r => fmtMoney(r.foreign_balance) },
                                        { key: 'rate_old', label: 'Rate (Old)', align: 'right' },
                                        { key: 'rate_new', label: 'Rate (New)', align: 'right' },
                                        { key: 'gain_loss', label: 'G/L', align: 'right', render: r => <span style={{ color: r.gain_loss >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>{fmtMoney(r.gain_loss)}</span> },
                                    ]}
                                    rows={selectedReval.accounts_revalued_json}
                                />
                            </>
                        )}
                    </Paper>
                )}
            </Stack>

            {/* New Reval Dialog */}
            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>New FX Revaluation</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth select label="Entity" value={form.entity_id}
                                onChange={e => setForm({ ...form, entity_id: e.target.value })}>
                                {entities.map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code} — {ent.entity_name} ({ent.functional_currency})</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth type="date" label="Period End Date" InputLabelProps={{ shrink: true }}
                                value={form.period_end_date} onChange={e => setForm({ ...form, period_end_date: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" fontWeight={600}>Foreign-Currency Accounts to Revalue</Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth type="number" label="Account ID" value={acctInput.account_id}
                                onChange={e => setAcctInput({ ...acctInput, account_id: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth label="Foreign Ccy" value={acctInput.foreign_currency}
                                onChange={e => setAcctInput({ ...acctInput, foreign_currency: e.target.value.toUpperCase().slice(0, 3) })} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth type="number" label="Foreign Balance" value={acctInput.foreign_balance}
                                onChange={e => setAcctInput({ ...acctInput, foreign_balance: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth type="number" label="Old Rate" value={acctInput.rate_old}
                                onChange={e => setAcctInput({ ...acctInput, rate_old: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button variant="outlined" size="small" fullWidth startIcon={<AddIcon />} onClick={addAccount}>Add</Button>
                        </Grid>
                        {form.accounts.length > 0 && (
                            <Grid item xs={12}>
                                <DrillTable
                                    columns={[
                                        { key: 'account_id', label: 'Acct ID' },
                                        { key: 'foreign_currency', label: 'Ccy' },
                                        { key: 'foreign_balance', label: 'Balance', align: 'right', render: r => fmtMoney(r.foreign_balance) },
                                        { key: 'rate_old', label: 'Rate Old', align: 'right' },
                                        { key: 'action', label: '', render: (_, i) => (
                                            <IconButton size="small" color="error" onClick={() => removeAccount(i)}><DeleteIcon fontSize="small" /></IconButton>
                                        )},
                                    ]}
                                    rows={form.accounts}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={runReval} disabled={!form.entity_id || form.accounts.length === 0 || loading} startIcon={<PlayArrowIcon />}>
                        Compute Revaluation
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
